import {AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import { Platform } from '@ionic/angular';
import { ModalService } from '@core/services/modal/modal.service';
import { SignaturePadRequest, isSignaturePadRequest } from '@core/models/Signature';
import {EditableSignatureOrigin, EditableSignatureRequest, isEditableSignatureRequest } from '@core/models/EditableSignature';
import {translate} from '@flowww/design-system';
import { SignaturePad } from '@flowww/signaturepad';

@Component({
    selector: 'app-signature-pad',
    templateUrl: './signature-pad.modal.html',
    styleUrls: ['./signature-pad.modal.scss'],
    standalone: false
})
export class SignaturePadModal implements OnInit, AfterViewInit {
    @Input() signaturePadRequest: SignaturePadRequest[];
    @Input() editableSignaturePadRequest: EditableSignatureRequest[];
    clientId: string;
    formId: string;
    modal: HTMLIonModalElement;
    signaturePadOptions: any;
    signaturePadIsEmpty = true;

    @ViewChild('signaturePad') signaturePad: SignaturePad;

    constructor(
        private modalService: ModalService,
        private platform: Platform
    ) {}

    get currentSignatureIndex(): number {
        if (this.signaturePadRequest) {
            return this.signaturePadRequest.findIndex(signaturePad => {
                return !signaturePad.image && !signaturePad.skip;
            });
        } else if (this.editableSignaturePadRequest) {
            return this.editableSignaturePadRequest.findIndex(signaturePad => {
                return !signaturePad.value;
            });
        }
        return -1; // Retornar -1 si ninguno de los arrays existe

    }

    get currentSignature(): SignaturePadRequest | EditableSignatureRequest {
        const index = this.currentSignatureIndex;

        if (this.signaturePadRequest && index >= 0 && index < this.signaturePadRequest.length) {
            return this.signaturePadRequest[index];
        } else if (this.editableSignaturePadRequest && index >= 0 && index < this.editableSignaturePadRequest.length) {
            return this.editableSignaturePadRequest[index];
        }
        return null; // Retornar null si no hay firma válida

    }

    get signInfoLabel(): string {
        const signature = this.currentSignature;

        if (isSignaturePadRequest(signature)) {
            return signature?.labelInfo;
        } else if (isEditableSignatureRequest(signature)) {
            switch (signature.formFieldType) {
                case EditableSignatureOrigin.DOCTOR: return translate('lbl_doctor_doctor_sign'); break;
                case EditableSignatureOrigin.CLIENT: return translate('lbl_doctor_client_sign'); break;
                case EditableSignatureOrigin.LEGAL_REP: return translate('lbl_doctor_legalrep_sign'); break;
            }
        }

    }

    get signButtonLabel(): string {
        const signature = this.currentSignature;

        if (isSignaturePadRequest(signature)) {
            if (signature.canEmptySign && this.isEmpty) {
                return 'lbl_continue_without_sign';
            }
            // Verificar que signaturePadRequest existe antes de acceder a su length
            return this.currentSignatureIndex < ((this.signaturePadRequest?.length || 0) - 1) ? 'lbl_sign_and_continue' : 'lbl_sign';
        }
        // Verificar que editableSignaturePadRequest existe antes de acceder a su length
        return this.currentSignatureIndex < ((this.editableSignaturePadRequest?.length || 0) - 1) ? 'lbl_sign_and_continue' : 'lbl_sign';
    }


    get signButtonDisabled(): boolean {
        const signature = this.currentSignature;

        if (isSignaturePadRequest(signature)) {
            return signature.canEmptySign ? false : this.isEmpty;
        }
        return false;
    }

    get isEmpty(): boolean {
        return this.signaturePad ? this.signaturePad.isEmpty() : true;
    }

    get isMobile(): boolean {
        return this.platform.is('mobile') && !this.platform.is('tablet');
    }

    ngOnInit(): void {
        this.signaturePadOptions = {
            backgroundColor: 'rgb(255, 255, 255)',
            canvasWidth: document.documentElement.clientWidth,
            canvasHeight: document.documentElement.clientHeight
        };
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            // Buscar el modal en el DOM - será el último modal creado
            const modalElements = document.querySelectorAll('ion-modal');
            if (modalElements.length > 0) {
                this.modal = modalElements[modalElements.length - 1] as HTMLIonModalElement;
                // console.log('Modal obtenido:', this.modal);
            }

            if (this.signaturePad) {
                this.signaturePad.clear();
                // console.log('SignaturePad inicializado correctamente');
            } else {
                // console.error('SignaturePad no se inicializó correctamente');
            }
        }, 500);
    }

    clearSignPad(): void {
        if (this.signaturePad) {
            this.signaturePad.clear();
            this.signaturePadIsEmpty = true;
        }
    }

    closeModal(): void {
        this.modalService.dismiss({ action: 'cancel' }, 'close', SignaturePadModalId);
    }

    async sign(): Promise<void> {
        try {
            const signature = this.currentSignature;

            if (!this.isEmpty) {
                if (!this.signaturePad) {
                    // console.error('SignaturePad no está disponible para firmar');
                    return;
                }

                let data = this.signaturePad.toDataURL('image/jpeg');

                if (!this.isMobile) {
                    try {
                        data = await this.rotateImage(data);
                    } catch (error) {
                        // console.error('Error al recortar imagen:', error);
                        // console.warn('Usando imagen original debido al error de recorte');
                    }
                }

                // Para escritorio, usar la imagen tal como está sin cropear

                if (isSignaturePadRequest(signature)) {
                    signature.image = data;
                } else if (isEditableSignatureRequest(signature)) {
                    signature.value = data;
                }

            } else {
                if (isSignaturePadRequest(signature)) {
                    signature.skip = true;
                }
            }

            if (!this.currentSignature) {
                this.modalService.dismiss({
                    action: 'sign',
                    signaturePadRequest: this.signaturePadRequest || this.editableSignaturePadRequest
                }, 'close', SignaturePadModalId);
            } else {
                this.clearSignPad();
            }
        } catch (error) {
            // console.error('Error en el proceso de firma:', error);
            // this.toastService.presentErrorToast('Error al procesar la firma');
        }
    }

    private async resizeImageToFitModal(dataUrl: string): Promise<string> {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    if (!ctx) {
                        resolve(dataUrl);
                        return;
                    }

                    // Obtener dimensiones del modal o usar dimensiones por defecto
                    let targetWidth = window.innerWidth;
                    let targetHeight = window.innerHeight - 107; // Espacio para header y footer

                    if (this.modal) {
                        try {
                            const modalWrapper = this.modal.shadowRoot?.querySelector('.modal-wrapper') ||
                                this.modal.querySelector('.modal-wrapper');
                            if (modalWrapper) {
                                targetWidth = modalWrapper.clientWidth;
                                targetHeight = modalWrapper.clientHeight - 107;
                            }
                        } catch (error) {
                            // console.warn('No se pudieron obtener dimensiones del modal, usando por defecto');
                        }
                    }

                    canvas.width = targetWidth;
                    canvas.height = targetHeight;

                    // Fondo blanco
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    // Calcular escala para mantener aspecto
                    const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
                    const scaledWidth = img.width * scale;
                    const scaledHeight = img.height * scale;

                    // Centrar la imagen
                    const x = (canvas.width - scaledWidth) / 2;
                    const y = (canvas.height - scaledHeight) / 2;

                    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
                    resolve(canvas.toDataURL('image/jpeg'));
                };

                img.onerror = () => {
                    // console.error('Error al cargar imagen para redimensionar');
                    resolve(dataUrl);
                };

                img.src = dataUrl;
            } catch (error) {
                // console.error('Error en resizeImageToFitModal:', error);
                resolve(dataUrl);
            }
        });
    }


    private rotateImage(data: string): Promise<string> {
        return new Promise(resolve => {
            const canvas = document.createElement('canvas');
            const image = new Image();

            image.src = data;
            image.onload = () => {
                canvas.width = document.documentElement.clientHeight;
                canvas.height = document.documentElement.clientWidth;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    // console.error('No se pudo obtener el contexto 2D para rotar');
                    resolve(data);
                    return;
                }

                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.height, canvas.width);
                ctx.rotate((90 * Math.PI) / 180);
                ctx.translate(0, -canvas.width);

                ctx.drawImage(image, 0, 0);
                resolve(canvas.toDataURL('image/jpeg'));
            };

            image.onerror = () => {
                // console.error('Error al cargar imagen para rotar');
                resolve(data);
            };
        });
    }

}

export const SignaturePadModalId = 'SIGNATURE_PAD_MODAL_ID';
export const SignaturePadModalCss = 'signatureModal';
